import React from "react";
import { Users, GraduationCap, User, Plus, X, Info } from "lucide-react";
import { Card, CardContent } from "src/components/ui/Card";
import { Label } from "src/components/ui/Label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  PersonCommandCombobox,
  PersonCommandOption,
} from "src/components/ui/PersonCommandCombobox";
import { SessionSchedulingType } from "src/services/sessionsService";

interface SessionRosterManagerProps {
  tutor: string;
  onTutorChange: (tutorId: string) => void;
  tutorOptions: PersonCommandOption[];
  student: string;
  onStudentChange: (studentId: string) => void;
  studentOptions: PersonCommandOption[];
  additionalStudentIds: string[];
  onAdditionalStudentChange: (studentId: string) => void;
  onRemoveAdditionalStudent: (studentId: string) => void;
  additionalStudentOptions: PersonCommandOption[];
  isParticipantsLoading: boolean;
  hasParticipantsError: boolean;
  participantsError: unknown;
  participantCount: number;
  schedulingType: SessionSchedulingType;
}

export const SessionRosterManager: React.FC<SessionRosterManagerProps> = ({
  tutor,
  onTutorChange,
  tutorOptions,
  student,
  onStudentChange,
  studentOptions,
  additionalStudentIds,
  onAdditionalStudentChange,
  onRemoveAdditionalStudent,
  additionalStudentOptions,
  isParticipantsLoading,
  hasParticipantsError,
  participantsError,
  participantCount,
  schedulingType,
}) => {
  const getStudentLabel = (studentId: string) =>
    studentOptions.find((option) => option.value === studentId)?.label || "Selected student";

  return (
    <Card className="border border-slate-200 rounded-xl shadow-xs overflow-hidden bg-white">
      <div className="bg-[#faf9fe] px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-xs font-mono text-purple-700 font-medium">STEP 2 • ROSTER SPECIFICATION</div>
          <div className="flex items-center gap-1.5">
            <h2 className="text-base font-bold text-slate-900">Participants & Access Roles</h2>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-600 transition p-0.5 rounded-full inline-flex items-center cursor-help"
                    aria-label="Participant access details"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs p-2.5 bg-slate-900 text-slate-100 shadow-lg">
                  Participant access is authorized strictly via session_participants.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">
          <Users className="w-3.5 h-3.5" />
          <span>{participantCount} Roster Member{participantCount === 1 ? "" : "s"}</span>
        </div>
      </div>

      <CardContent className="p-6 space-y-5">
        {/* TUTOR SELECTOR */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="tutor" className="text-xs font-semibold text-slate-700">
              Assigned Tutor (Primary Host) *
            </Label>
            {tutor && (
              <span className="text-xs font-mono text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                Radar Active in Step 3
              </span>
            )}
          </div>
          <PersonCommandCombobox
            id="tutor"
            value={tutor}
            onChange={onTutorChange}
            options={tutorOptions}
            placeholder="Search and select a tutor"
            searchPlaceholder="Search tutor by name, subject, expertise..."
            emptyLabel={isParticipantsLoading ? "Loading tutors..." : "No tutors found."}
            createLabel="Create new tutor"
            icon={<GraduationCap className="h-4 w-4" />}
            disabled={isParticipantsLoading}
          />
          {hasParticipantsError && (
            <p className="text-xs text-rose-600">
              Unable to load tutors: {participantsError instanceof Error ? participantsError.message : "Please try again."}
            </p>
          )}
        </div>

        {/* PRIMARY STUDENT SELECTOR */}
        <div className="space-y-2">
          <Label htmlFor="student" className="text-xs font-semibold text-slate-700">
            Primary Enrolled Student *
          </Label>
          <PersonCommandCombobox
            id="student"
            value={student}
            onChange={onStudentChange}
            options={studentOptions}
            placeholder="Search and select student"
            searchPlaceholder="Search student by name, grade, email..."
            emptyLabel={isParticipantsLoading ? "Loading students..." : "No students found."}
            createLabel="Create new student"
            icon={<User className="h-4 w-4" />}
            disabled={isParticipantsLoading || schedulingType === "recurring"}
          />
        </div>

        {/* ADDITIONAL STUDENTS / PEERS ROSTER */}
        <div className="space-y-3 rounded-xl border border-dashed border-slate-300 p-4 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="additional-student" className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                Additional Students (Group Tutoring Support)
              </Label>
              <p className="text-xs text-slate-500">
                Optional. Add peers to convert this tutoring session into a group meeting.
              </p>
            </div>
            <span className="text-xs font-mono text-slate-600 font-semibold bg-white px-2 py-0.5 rounded border border-slate-200">
              {additionalStudentIds.length} Peer{additionalStudentIds.length === 1 ? "" : "s"}
            </span>
          </div>

          <PersonCommandCombobox
            id="additional-student"
            value=""
            onChange={onAdditionalStudentChange}
            options={additionalStudentOptions}
            placeholder="Search and add additional student..."
            searchPlaceholder="Search by name, grade..."
            emptyLabel={isParticipantsLoading ? "Loading students..." : "No additional students found."}
            createLabel="Create new student"
            icon={<Plus className="h-4 w-4" />}
            disabled={isParticipantsLoading || additionalStudentOptions.length === 0}
          />

          {additionalStudentIds.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {additionalStudentIds.map((studentId) => (
                <span
                  key={studentId}
                  className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-800"
                >
                  <span>{getStudentLabel(studentId)}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveAdditionalStudent(studentId)}
                    className="rounded-full p-0.5 hover:bg-blue-100 text-blue-600 transition"
                    aria-label={`Remove ${getStudentLabel(studentId)}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

